import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import evoGameTechLogo from '../assets/evogametech-logo.png';
import { Wallet, Landmark, BarChart3, LogOut, Menu, X, ChevronLeft, Moon, Sun, ChevronDown, ChevronRight, Plus, Edit, Trash2, Shield, ShieldCheck, Users, Bot, Package, Cpu, DollarSign, UsersRound, FileSpreadsheet, TrendingUp, Trophy, BarChart2, Image, CalendarDays, Smartphone, Mail, Calculator, MessageCircle, Sparkles, LineChart, FolderOpen, Cable, LayoutDashboard, Database, ShieldAlert, UploadCloud, FileJson, Network, Globe, Brain,
} from 'lucide-react';
import { CarteraCuentaCobro } from './CarteraCuentaCobro';
import { usePortfolio } from '../contexts/PortfolioContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export default function Layout({ children, onLogout, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collectorV2Expanded, setCollectorV2Expanded] = useState(
    () => typeof window !== 'undefined' && (
      window.location.pathname === '/collector-v2' || window.location.pathname.startsWith('/collector-v2/')
    )
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [portfoliosExpanded, setPortfoliosExpanded] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [portfolioName, setPortfolioName] = useState('');
  const [portfolioDescription, setPortfolioDescription] = useState('');
  const [titularDestino, setTitularDestino] = useState('');
  const [cbuDestino, setCbuDestino] = useState('');
  const [validarDestinatario, setValidarDestinatario] = useState(true);
  const [silentMode, setSilentMode] = useState(false);
  // Cuenta que la campanita le pide al club (por cartera). Vacío = la cuenta global del CRM.
  const [recordatorioAlias, setRecordatorioAlias] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const { portfolios, activePortfolio, loading, createPortfolio, updatePortfolio, deletePortfolio, switchPortfolio } = usePortfolio();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleCreatePortfolio = async () => {
    if (!portfolioName.trim()) {
      toast.error('El nombre de la cartera es requerido');
      return;
    }
    
    if (!titularDestino.trim()) {
      toast.error('El titular destino es requerido');
      return;
    }
    
    if (!cbuDestino.trim() || cbuDestino.length !== 22) {
      toast.error('El CBU debe tener exactamente 22 dígitos');
      return;
    }

    try {
      await createPortfolio({
        name: portfolioName.trim(),
        titular_destino: titularDestino.trim(),
        cbu_destino: cbuDestino.trim(),
        validar_destinatario: validarDestinatario
      });
      toast.success('Cartera creada exitosamente');
      setPortfolioName('');
      setTitularDestino('');
      setCbuDestino('');
      setValidarDestinatario(true);
      setCreateDialogOpen(false);
    } catch (error) {
      toast.error('Error al crear la cartera');
    }
  };

  const handleEditPortfolio = async () => {
    if (!portfolioName.trim()) {
      toast.error('El nombre de la cartera es requerido');
      return;
    }
    
    if (!titularDestino.trim()) {
      toast.error('El titular destino es requerido');
      return;
    }
    
    if (!cbuDestino.trim() || cbuDestino.length !== 22) {
      toast.error('El CBU debe tener exactamente 22 dígitos');
      return;
    }
    

    try {
      await updatePortfolio(selectedPortfolio.id, {
        name: portfolioName.trim(),
        titular_destino: titularDestino.trim(),
        cbu_destino: cbuDestino.trim(),
        validar_destinatario: validarDestinatario,
        silent_mode: silentMode,
        recordatorio_alias: recordatorioAlias
      });
      toast.success('Cartera actualizada exitosamente');
      setEditDialogOpen(false);
      setSelectedPortfolio(null);
      setPortfolioName('');
      setTitularDestino('');
      setCbuDestino('');
      setValidarDestinatario(true);
    } catch (error) {
      toast.error('Error al actualizar la cartera');
    }
  };

  const handleDeletePortfolio = async () => {
    try {
      await deletePortfolio(selectedPortfolio.id);
      toast.success('Cartera eliminada exitosamente');
      setDeleteDialogOpen(false);
      setSelectedPortfolio(null);
    } catch (error) {
      toast.error('Error al eliminar la cartera');
    }
  };

  const openEditDialog = (portfolio) => {
    setSelectedPortfolio(portfolio);
    setPortfolioName(portfolio.name);
    setTitularDestino(portfolio.titular_destino || '');
    setCbuDestino(portfolio.cbu_destino || '');
    setValidarDestinatario(portfolio.validar_destinatario !== undefined ? portfolio.validar_destinatario : true);
    setSilentMode(portfolio.silent_mode || false);
    setRecordatorioAlias(portfolio.recordatorio_alias || '');
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (portfolio) => {
    setSelectedPortfolio(portfolio);
    setDeleteDialogOpen(true);
  };

  const handleSwitchPortfolio = (portfolio) => {
    switchPortfolio(portfolio);
    navigate('/dashboard');
    setSidebarOpen(false);
  };

  // Menú items (mostrar siempre Gestión de Usuarios para admin/cartera)
  const menuItems = [
    {
      name: 'Movimientos Financieros',
      path: '/dashboard',
      icon: Wallet,
      description: 'Gestión de comprobantes y cuentas',
      permKey: 'movimientos'
    },
    {
      name: 'Bancos',
      path: '/bancos',
      icon: Landmark,
      description: 'Cuentas bancarias y movimientos',
      permKey: 'bancos'
    },
    {
      name: 'Estadísticas',
      path: '/analytics/remitentes',
      icon: BarChart3,
      description: 'Análisis de remitentes y montos',
      permKey: 'estadisticas'
    },
    {
      name: 'Flyers',
      path: '/flyers',
      icon: Image,
      description: 'Gestión de flyers para difusión',
      permKey: 'flyers'
    },
    {
      name: 'Grilla semanal',
      path: '/grilla',
      icon: CalendarDays,
      description: 'Torneos MTT de la unión en PP',
      permKey: 'flyers'
    },
    {
      name: 'Gestión de Usuarios',
      path: '/users',
      icon: Users,
      description: 'Administrar clientes y auditores',
      adminOnly: true
    },
    {
      name: 'Correo',
      path: '/emails',
      icon: Mail,
      description: 'Bandeja de entrada - datos@evogametech.com',
      adminOnly: true
    },
    {
      name: 'Admin',
      path: '/admin',
      icon: Shield,
      description: 'Gestión de trabajadores y permisos',
      adminOnly: true
    }
  ];

  // Filtrar menú según rol y permisos del usuario
  const userRole = user?.role || 'admin';
  const userPermissions = user?.permissions || [];
  
  const filteredMenuItems = userRole === 'admin' 
    ? menuItems 
    : menuItems.filter(item => {
        if (item.adminOnly) return false;
        if (!item.permKey) return false;
        return userPermissions.includes(item.permKey);
      });

  // Helper: verifica si el usuario puede ver un módulo
  const canView = (permKey) => {
    if (userRole === 'admin') return true;
    if (permKey === '__admin__') return false; // Solo admin
    return userPermissions.includes(permKey);
  };

  // Proteccion de rutas: redirigir workers a su primer modulo permitido
  const ROUTE_PERM_MAP = {
    '/dashboard': 'movimientos', '/bancos': 'bancos', '/analytics/remitentes': 'estadisticas',
    '/flyers': 'flyers', '/telegram-bot': 'telegram', '/reconciliation': 'conciliacion',
    '/poker/consolidacion': 'consolidacion', '/whatsapp-live': 'whatsapp',
    '/poker/cierre-semanal': 'cierre_semanal',
    '/poker/api-pppoker': 'pppoker',
    '/collector-v2': 'pppoker', '/collector-v2/arquitectura': 'pppoker', '/collector-v2/alertas': 'pppoker',
    '/collector-v2/collector': 'pppoker', '/collector-v2/importar': 'pppoker', '/collector-v2/manos': 'pppoker',
    '/collector-v2/jugadores': 'pppoker', '/collector-v2/relaciones': 'pppoker', '/collector-v2/network': 'pppoker',
    '/poker/agentes-united': 'agentes_united',
    '/poker/super-union': 'super_union', '/poker/cierre-super-union': 'super_union', '/poker/super-union-query': 'ppsr_informe',
    '/poker/ppst-informe': 'ppst_informe', '/users': '__admin__', '/admin': '__admin__',
    '/statistics': 'estadisticas',
  };

  // Auto-expandir el colapsable Collector V2 cuando la URL entra a /collector-v2/*
  useEffect(() => {
    if (location.pathname === '/collector-v2' || location.pathname.startsWith('/collector-v2/')) {
      setCollectorV2Expanded(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (userRole !== 'worker') return;
    const requiredPerm = ROUTE_PERM_MAP[location.pathname];
    if (requiredPerm && !canView(requiredPerm)) {
      const allowed = Object.entries(ROUTE_PERM_MAP).find(([, p]) => canView(p));
      navigate(allowed ? allowed[0] : '/dashboard', { replace: true });
    }
  }, [location.pathname, userRole]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 transition-colors duration-300">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {/* Desktop sidebar toggle */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="layout-desktop-sidebar-toggle"
              className="hidden lg:flex"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
            >
              {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="layout-mobile-sidebar-toggle"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 sm:w-[52px] sm:h-[52px] shrink-0 overflow-hidden rounded-xl shadow-lg" aria-hidden="true">
                <img src={evoGameTechLogo} alt="" className="absolute max-w-none" style={{ width: '108%', height: '110%', left: '-4%', top: '-5%' }} />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  EvoGameTech
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Hola, {user?.username || 'Usuario'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="layout-dark-mode-toggle"
              onClick={toggleDarkMode}
              className="h-9 w-9 p-0"
              title={darkMode ? "Modo claro" : "Modo oscuro"}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </Button>

            <Link to="/seguridad" data-testid="nav-seguridad">
              <Button
                variant="ghost"
                title="Seguridad y passkeys"
                className="flex items-center gap-2 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-900/20"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden lg:inline">Seguridad</span>
              </Button>
            </Link>


            <Button 
              variant="ghost" 
              onClick={onLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`
            h-screen overflow-hidden
            fixed lg:static inset-y-0 left-0 z-40 
            bg-white dark:bg-gray-800 
            transform transition-all duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'}
            w-72
            mt-[73px] lg:mt-0
          `}
        >
          <nav className="p-4 space-y-2 overflow-y-auto h-full">
            {!sidebarCollapsed && (
              <p className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider transition-colors">
                Menú Principal
              </p>
            )}
            
            {/* Movimientos Financieros - Expandible con Carteras */}
            {canView("movimientos") && (<div>
              <div
                data-testid="nav-movimientos-financieros"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !sidebarCollapsed) setPortfoliosExpanded(!portfoliosExpanded); }}
                onClick={() => !sidebarCollapsed && setPortfoliosExpanded(!portfoliosExpanded)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer
                  ${isActive('/dashboard')
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                title={sidebarCollapsed ? 'Movimientos Financieros' : ''}
              >
                <Wallet className={`w-5 h-5 ${isActive('/dashboard') ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${isActive('/dashboard') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        Movimientos Financieros
                      </p>
                      <p className={`text-xs ${isActive('/dashboard') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Gestión de comprobantes y cuentas
                      </p>
                    </div>
                    {portfoliosExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </>
                )}
              </div>

              {/* Lista de Carteras */}
              {!sidebarCollapsed && portfoliosExpanded && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                  {loading ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 py-2">Cargando carteras...</p>
                  ) : portfolios.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 py-2">No hay carteras creadas</p>
                  ) : (
                    portfolios.map((portfolio) => (
                      <div
                        key={portfolio.id}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-md transition-all group
                          ${activePortfolio?.id === portfolio.id
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 border-l-2 border-indigo-500' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }
                        `}
                      >
                        <button
                          data-testid={`portfolio-select-${portfolio.id}`}
                          onClick={() => handleSwitchPortfolio(portfolio)}
                          className="flex-1 text-left min-w-0"
                        >
                          <p className={`text-sm font-medium truncate ${
                            activePortfolio?.id === portfolio.id
                              ? 'text-indigo-600 dark:text-indigo-400' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {portfolio.name}
                          </p>
                          {portfolio.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {portfolio.description}
                            </p>
                          )}
                        </button>
                        <div className="flex gap-1 transition-opacity flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`portfolio-edit-${portfolio.id}`}
                            className="h-7 w-7 p-0 flex items-center justify-center hover:bg-yellow-50 hover:text-yellow-600 dark:hover:bg-yellow-900/20 text-gray-400 hover:scale-110 transition-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(portfolio);
                            }}
                            title="Editar cartera"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`portfolio-delete-${portfolio.id}`}
                            className="h-7 w-7 p-0 flex items-center justify-center hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 text-gray-400 hover:scale-110 transition-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteDialog(portfolio);
                            }}
                            title="Eliminar cartera"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {/* Botón Nueva Cartera */}
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="portfolio-create-open-button"
                        className="w-full mt-2 justify-start gap-2 border-dashed"
                      >
                        <Plus className="w-4 h-4" />
                        Nueva Cartera
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Crear Nueva Cartera</DialogTitle>
                        <DialogDescription>
                          Crea una nueva cartera para organizar tus movimientos financieros.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nombre de la Cartera *</Label>
                          <Input
                            id="name"
                            placeholder="Ej: Cartera Principal, Sucursal Norte"
                            value={portfolioName}
                            onChange={(e) => setPortfolioName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="titular">Titular Destino *</Label>
                          <Input
                            id="titular"
                            placeholder="Nombre completo del titular"
                            value={titularDestino}
                            onChange={(e) => setTitularDestino(e.target.value)}
                          />
                          <p className="text-xs text-gray-500">Los comprobantes deben tener este titular para ser aceptados</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cbu">CBU Destino *</Label>
                          <Input
                            id="cbu"
                            placeholder="22 dígitos del CBU"
                            maxLength={22}
                            value={cbuDestino}
                            onChange={(e) => setCbuDestino(e.target.value)}
                          />
                          <p className="text-xs text-gray-500">Los comprobantes deben tener este CBU para ser aceptados</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="validarDestinatario"
                            checked={validarDestinatario}
                            onChange={(e) => setValidarDestinatario(e.target.checked)}
                            className="h-4 w-4"
                          />
                          <Label htmlFor="validarDestinatario" className="font-normal cursor-pointer">
                            Validar titular y CBU en los comprobantes
                          </Label>
                        </div>
                        <p className="text-xs text-gray-500">
                          ℹ️ Si está activado, solo se aceptarán comprobantes que coincidan exactamente con el titular y CBU configurados.
                          Desactívalo si esta cartera recibe pagos de múltiples destinatarios.
                        </p>
                        
                        <div className="flex items-center space-x-2 mt-4">
                          <input
                            type="checkbox"
                            id="silentMode"
                            checked={silentMode}
                            onChange={(e) => setSilentMode(e.target.checked)}
                            className="h-4 w-4"
                          />
                          <Label htmlFor="silentMode" className="font-normal cursor-pointer">
                            🔇 Modo Silencioso (Bot de Telegram)
                          </Label>
                        </div>
                        <p className="text-xs text-gray-500">
                          ℹ️ Si está activado, el bot NO enviará mensajes de confirmación, errores ni solicitudes de CUIL.
                          Todo se procesa en segundo plano (ideal para auditoría silenciosa).
                        </p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleCreatePortfolio}>
                          Crear Cartera
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>)}

            {/* Bancos */}
            {canView("bancos") && (<Link
              to="/bancos"
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                ${isActive('/bancos')
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-bancos"
              title={sidebarCollapsed ? 'Bancos' : ''}
            >
              <Landmark className={`w-5 h-5 mt-0.5 ${isActive('/bancos') ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
              {!sidebarCollapsed && (
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${isActive('/bancos') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Bancos
                  </p>
                  <p className={`text-xs ${isActive('/bancos') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                    Cuentas bancarias y movimientos
                  </p>
                </div>
              )}
            </Link>)}

            {/* Estadísticas */}
            {canView("estadisticas") && (<Link
              to="/analytics/remitentes"
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                ${isActive('/analytics/remitentes')
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-statistics"
              title={sidebarCollapsed ? 'Estadísticas' : ''}
            >
              <BarChart3 className={`w-5 h-5 mt-0.5 ${isActive('/analytics/remitentes') ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
              {!sidebarCollapsed && (
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${isActive('/analytics/remitentes') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Estadísticas
                  </p>
                  <p className={`text-xs ${isActive('/analytics/remitentes') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                    Análisis de remitentes y montos
                  </p>
                </div>
              )}
            </Link>)}

            {/* Flyers - Gestión de flyers */}
            {canView("flyers") && (<Link
              to="/flyers"
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                ${isActive('/flyers')
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-flyers"
              title={sidebarCollapsed ? 'Flyers' : ''}
            >
              <Image className={`w-5 h-5 mt-0.5 ${isActive('/flyers') ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
              {!sidebarCollapsed && (
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${isActive('/flyers') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Flyers
                  </p>
                  <p className={`text-xs ${isActive('/flyers') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                    Gestión de flyers para difusión
                  </p>
                </div>
              )}
            </Link>)}

            {/* Grilla semanal - Torneos MTT de la unión */}
            {canView("flyers") && (<Link
              to="/grilla"
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                ${isActive('/grilla')
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-grilla"
              title={sidebarCollapsed ? 'Grilla semanal' : ''}
            >
              <CalendarDays className={`w-5 h-5 mt-0.5 ${isActive('/grilla') ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
              {!sidebarCollapsed && (
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${isActive('/grilla') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Grilla semanal
                  </p>
                  <p className={`text-xs ${isActive('/grilla') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                    Torneos MTT de la unión en PP
                  </p>
                </div>
              )}
            </Link>)}


            {/* Bot de Telegram */}
            {canView("telegram") && (
              <Link
                to="/telegram-bot"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/telegram-bot')
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-telegram"
                title={sidebarCollapsed ? 'Bot de Telegram' : ''}
              >
                <Bot className={`w-5 h-5 mt-0.5 ${isActive('/telegram-bot') ? 'text-white' : 'text-blue-500 dark:text-blue-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/telegram-bot') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Bot de Telegram
                    </p>
                    <p className={`text-xs ${isActive('/telegram-bot') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Gestionar grupos y actividad
                    </p>
                  </div>
                )}
              </Link>
            )}


            {/* Gestión de Usuarios - Solo para admin */}
            {canView("__admin__") && (
              <Link
                to="/users"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/users')
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-users"
                title={sidebarCollapsed ? 'Gestión de Usuarios' : ''}
              >
                <Users className={`w-5 h-5 mt-0.5 ${isActive('/users') ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/users') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Gestión de Usuarios
                    </p>
                    <p className={`text-xs ${isActive('/users') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Administrar clientes y auditores
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* WhatsApp Live */}
            {canView("whatsapp") && (
              <Link
                to="/whatsapp-live"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/whatsapp-live')
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-whatsapp-live"
                title={sidebarCollapsed ? 'WhatsApp Live' : ''}
              >
                <MessageCircle className={`w-5 h-5 mt-0.5 ${isActive('/whatsapp-live') ? 'text-white' : 'text-green-500 dark:text-green-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/whatsapp-live') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      📱 WhatsApp Live
                    </p>
                    <p className={`text-xs ${isActive('/whatsapp-live') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Mensajes en tiempo real
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* Separador - Sección Poker */}
            {!sidebarCollapsed && (userRole === 'admin' || ['consolidacion','cierre_semanal','pppoker','agentes_united','super_union','ppsr_informe','ppst_informe'].some(p => userPermissions.includes(p))) && (
              <div className="my-4">
                <div className="border-t border-gray-300 dark:border-gray-600"></div>
                <p className="px-4 py-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider transition-colors">
                  🎰 Unión de Poker
                </p>
              </div>
            )}

            {/* Consolidación */}
            {canView("consolidacion") && (
              <Link
                to="/poker/consolidacion"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/poker/consolidacion')
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-consolidacion"
                title={sidebarCollapsed ? 'Consolidación' : ''}
              >
                <FileSpreadsheet className={`w-5 h-5 mt-0.5 ${isActive('/poker/consolidacion') ? 'text-white' : 'text-violet-500 dark:text-violet-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/poker/consolidacion') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Consolidación
                    </p>
                    <p className={`text-xs ${isActive('/poker/consolidacion') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Vista consolidada de datos
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* Cierre Semanal */}
            {canView("cierre_semanal") && (
              <Link
                to="/poker/cierre-semanal"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/poker/cierre-semanal')
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-cierre-semanal"
                title={sidebarCollapsed ? 'Cierre Semanal' : ''}
              >
                <Calculator className={`w-5 h-5 mt-0.5 ${isActive('/poker/cierre-semanal') ? 'text-white' : 'text-green-500 dark:text-green-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/poker/cierre-semanal') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Cierre Semanal
                    </p>
                    <p className={`text-xs ${isActive('/poker/cierre-semanal') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Liquidación PPPoker
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* API PPPoker — consultas a la API oficial */}
            {canView("pppoker") && (
              <Link
                to="/poker/api-pppoker"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/poker/api-pppoker')
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-api-pppoker"
                title={sidebarCollapsed ? 'API PPPoker' : ''}
              >
                <Cable className={`w-5 h-5 mt-0.5 ${isActive('/poker/api-pppoker') ? 'text-white' : 'text-violet-500 dark:text-violet-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/poker/api-pppoker') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      API PPPoker
                    </p>
                    <p className={`text-xs ${isActive('/poker/api-pppoker') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Saldos, clubes y agentes en vivo
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* Collector V2 — colapsable con 9 sub-items */}
            {canView("pppoker") && (() => {
              const isCollectorV2Section = location.pathname === '/collector-v2' || location.pathname.startsWith('/collector-v2/');
              const isOpen = collectorV2Expanded;
              const subItems = [
                { to: '/collector-v2',                 label: 'Collector V2',    icon: Cable,           testid: 'nav-cv2-collector',     end: true  },
                { to: '/collector-v2/alertas',         label: 'Alertas V2',      icon: ShieldAlert,     testid: 'nav-cv2-alertas',       end: false },
                { to: '/collector-v2/manos',           label: 'Manos',           icon: FileJson,        testid: 'nav-cv2-manos',         end: false },
                { to: '/collector-v2/jugadores',       label: 'Jugadores',       icon: UsersRound,      testid: 'nav-cv2-jugadores',     end: false },
                { to: '/collector-v2/relaciones',      label: 'Relaciones',      icon: Network,         testid: 'nav-cv2-relaciones',    end: false },
                { to: '/collector-v2/network',         label: 'Network Graph',   icon: Globe,           testid: 'nav-cv2-network',       end: false },
                { to: '/collector-v2/brain',           label: 'Brain',           icon: Brain,           testid: 'nav-cv2-brain',         end: false },
              ];
              return (
                <div data-testid="collector-v2-section">
                  {/* Header colapsable */}
                  <button
                    type="button"
                    onClick={() => setCollectorV2Expanded((v) => !v)}
                    className={`
                      w-full flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                      ${isCollectorV2Section
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                    data-testid="nav-collector-v2"
                    title={sidebarCollapsed ? 'Collector V2' : ''}
                  >
                    <Cable className={`w-5 h-5 mt-0.5 ${isCollectorV2Section ? 'text-white' : 'text-cyan-500 dark:text-cyan-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                    {!sidebarCollapsed && (
                      <>
                        <div className="flex-1 text-left">
                          <p className={`font-semibold text-sm ${isCollectorV2Section ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                            Collector V2
                          </p>
                          <p className={`text-xs ${isCollectorV2Section ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                            Captura PPPoker en tiempo real
                          </p>
                        </div>
                        {isOpen
                          ? <ChevronDown className={`w-4 h-4 mt-1 ${isCollectorV2Section ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} />
                          : <ChevronRight className={`w-4 h-4 mt-1 ${isCollectorV2Section ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} />
                        }
                      </>
                    )}
                  </button>

                  {/* Sub-items */}
                  {isOpen && !sidebarCollapsed && (
                    <div className="ml-4 mt-1 mb-2 space-y-0.5 border-l border-gray-200 dark:border-gray-700 pl-2" data-testid="collector-v2-subnav">
                      {subItems.map((it) => {
                        const Icon = it.icon;
                        const active = it.end
                          ? location.pathname === it.to
                          : location.pathname === it.to || location.pathname.startsWith(it.to + '/');
                        return (
                          <Link
                            key={it.to}
                            to={it.to}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={it.testid}
                            className={`
                              flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all
                              ${active
                                ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 font-semibold'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-gray-200'
                              }
                            `}
                          >
                            <Icon className={`w-4 h-4 ${active ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-400 dark:text-gray-500'}`} />
                            <span>{it.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Agentes United */}
            {canView("agentes_united") && (
              <Link
                to="/poker/agentes-united"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                  ${isActive('/poker/agentes-united')
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-agentes-united"
                title={sidebarCollapsed ? 'Agentes United' : ''}
              >
                <UsersRound className={`w-5 h-5 mt-0.5 ${isActive('/poker/agentes-united') ? 'text-white' : 'text-orange-500 dark:text-orange-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/poker/agentes-united') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Agentes United
                    </p>
                    <p className={`text-xs ${isActive('/poker/agentes-united') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Gestión de agentes de poker
                    </p>
                  </div>
                )}
              </Link>
            )}

            {/* Super Unión - Solo para admin/cartera */}
            {canView("super_union") && (
              <>
                <Link
                  to="/poker/cierre-super-union"
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                    ${isActive('/poker/cierre-super-union')
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  data-testid="nav-cierre-super-union"
                  title={sidebarCollapsed ? 'Cierre Super Union' : ''}
                >
                  <Calculator className={`w-5 h-5 mt-0.5 ${isActive('/poker/cierre-super-union') ? 'text-white' : 'text-emerald-500 dark:text-emerald-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                  {!sidebarCollapsed && (
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${isActive('/poker/cierre-super-union') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        Cierre Super Union
                      </p>
                      <p className={`text-xs ${isActive('/poker/cierre-super-union') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Balance semanal por unión
                      </p>
                    </div>
                  )}
                </Link>
              </>
            )}

            {canView("super_union") && (
              <>
                <Link
                  to="/poker/super-union"
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                    ${isActive('/poker/super-union')
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  data-testid="nav-super-union"
                  title={sidebarCollapsed ? 'Super Unión - Import' : ''}
                >
                  <TrendingUp className={`w-5 h-5 mt-0.5 ${isActive('/poker/super-union') ? 'text-white' : 'text-blue-500 dark:text-blue-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                  {!sidebarCollapsed && (
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${isActive('/poker/super-union') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        Super Unión - Import
                      </p>
                      <p className={`text-xs ${isActive('/poker/super-union') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Importar archivos PPSR/PPST
                      </p>
                    </div>
                  )}
                </Link>

                <Link
                  to="/poker/super-union-query"
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-start gap-3 px-4 py-3 rounded-lg transition-all
                    ${isActive('/poker/super-union-query')
                      ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  data-testid="nav-ppsr-informe"
                  title={sidebarCollapsed ? 'PPSR Informe' : ''}
                >
                  <Users className={`w-5 h-5 mt-0.5 ${isActive('/poker/super-union-query') ? 'text-white' : 'text-violet-500 dark:text-violet-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                  {!sidebarCollapsed && (
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${isActive('/poker/super-union-query') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        PPSR Informe
                      </p>
                      <p className={`text-xs ${isActive('/poker/super-union-query') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Cash Games - Informes detallados
                      </p>
                    </div>
                  )}
                </Link>

              </>
            )}

            {canView("ppst_informe") && (
                <Link
                  to="/poker/ppst-informe"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive('/poker/ppst-informe')
                      ? 'bg-gradient-to-r from-purple-600 to-violet-600 shadow-lg'
                      : 'hover:bg-violet-50 dark:hover:bg-gray-800'
                  } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                  data-testid="nav-ppst-informe"
                  title={sidebarCollapsed ? 'PPST Informe' : ''}
                >
                  <Trophy className={`w-5 h-5 mt-0.5 ${isActive('/poker/ppst-informe') ? 'text-white' : 'text-purple-500 dark:text-purple-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                  {!sidebarCollapsed && (
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${isActive('/poker/ppst-informe') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        PPST Informe
                      </p>
                      <p className={`text-xs ${isActive('/poker/ppst-informe') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Torneos - Eventos y estadísticas
                      </p>
                    </div>
                  )}
                </Link>
            )}



            {/* ADMIN - Solo visible para administradores */}
            {canView("__admin__") && (
              <Link
                to="/admin"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive('/admin')
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg'
                    : 'hover:bg-indigo-50 dark:hover:bg-gray-800'
                } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                data-testid="nav-admin"
                title={sidebarCollapsed ? 'Admin' : ''}
              >
                <Shield className={`w-5 h-5 mt-0.5 ${isActive('/admin') ? 'text-white' : 'text-indigo-500 dark:text-indigo-400'} ${sidebarCollapsed ? 'mt-0' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isActive('/admin') ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                      Admin
                    </p>
                    <p className={`text-xs ${isActive('/admin') ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                      Trabajadores y permisos
                    </p>
                  </div>
                )}
              </Link>
            )}


          </nav>

          {/* Dialogs para Editar y Eliminar */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Editar Cartera</DialogTitle>
                <DialogDescription>
                  Actualiza la configuración de la cartera.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nombre de la Cartera *</Label>
                  <Input
                    id="edit-name"
                    placeholder="Ej: Cartera Principal, Sucursal Norte"
                    value={portfolioName}
                    onChange={(e) => setPortfolioName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-titular">Titular Destino *</Label>
                  <Input
                    id="edit-titular"
                    placeholder="Nombre completo del titular"
                    value={titularDestino}
                    onChange={(e) => setTitularDestino(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Los comprobantes deben tener este titular para ser aceptados (si la validación está activa)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-cbu">CBU Destino *</Label>
                  <Input
                    id="edit-cbu"
                    placeholder="22 dígitos del CBU"
                    maxLength={22}
                    value={cbuDestino}
                    onChange={(e) => setCbuDestino(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Los comprobantes deben tener este CBU para ser aceptados (si la validación está activa)</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-validarDestinatario"
                    checked={validarDestinatario}
                    onChange={(e) => setValidarDestinatario(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit-validarDestinatario" className="font-normal cursor-pointer">
                    Validar titular y CBU en los comprobantes
                  </Label>
                </div>
                <p className="text-xs text-gray-500">
                  ℹ️ Si está activado, solo se aceptarán comprobantes que coincidan exactamente con el titular y CBU configurados.
                  Desactívalo si esta cartera recibe pagos de múltiples destinatarios.
                </p>
                
                <div className="flex items-center space-x-2 mt-4">
                  <input
                    type="checkbox"
                    id="edit-silentMode"
                    checked={silentMode}
                    onChange={(e) => setSilentMode(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit-silentMode" className="font-normal cursor-pointer">
                    🔇 Modo Silencioso (Bot de Telegram)
                  </Label>
                </div>
                <p className="text-xs text-gray-500">
                  ℹ️ Si está activado, el bot NO enviará mensajes de confirmación, errores ni solicitudes de CUIL.
                  Todo se procesa en segundo plano (ideal para auditoría silenciosa).
                </p>
                
                <CarteraCuentaCobro value={recordatorioAlias} onChange={setRecordatorioAlias}
                  abierto={editDialogOpen} />

              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleEditPortfolio}>
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Eliminar Cartera</DialogTitle>
                <DialogDescription>
                  ¿Estás seguro de que deseas eliminar la cartera "{selectedPortfolio?.name}"? 
                  Esta acción eliminará también todas las cuentas y recibos asociados y no se puede deshacer.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button variant="destructive" onClick={handleDeletePortfolio}>
                  Eliminar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


        </aside>

        {/* Main content */}
        <main className={`flex-1 overflow-y-auto max-h-screen transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-0' : ''}`}>
          <div className="p-4 md:p-6 lg:p-8">
            {children}
          </div>
        </main>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
// Force rebuild: 1766960207
